import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { goTo } from '@adornis/router/client/open-href.js';
import '@chancenwerk/components/chancen-components-auto-navigation.js';
import { type SidebarSectionDefinition } from '@chancenwerk/components/chancen-components-auto-navigation.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import dashboardLogo from '../assets/icons/Home.svg';
import graduateHatLogo from '../assets/icons/graduate_hat.svg';
import searchLogo from '../assets/icons/search.svg';
import logo from '../assets/logo_chancencampus_white.svg';
import { CampusFirstLevelPage, CampusRoutes } from '../routes.js';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const version = require('../../../package.json').version;

const navigation: SidebarSectionDefinition[] = [
  {
    name: 'Adminansicht',
    subsections: [
      {
        name: 'Admin',
        links: [
          {
            label: 'Neuigkeiten',
            route: CampusRoutes.notifications,
            permission: ChancenPermission.global.EDIT_NOTIFICATIONS,
          },
          {
            label: 'Gruppen',
            route: CampusRoutes.groups,
            permission: ChancenPermission.campus.EDIT_GROUPS,
          },
          {
            label: 'Zugänge',
            route: CampusRoutes.childrenStatistics,
            permission: ChancenPermission.campus.READ_STATISTICS,
          },
          {
            label: 'Events',
            route: CampusRoutes.eventAdmin,
            permission: ChancenPermission.campus.ADMIN_EVENTS,
          },
          {
            label: 'Blanko-Accounts',
            route: CampusRoutes.blankoAccounts,
            icon: 'people',
            permission: ChancenPermission.campus.CREATE_BLANKO,
          },
          {
            label: 'Wirkungsmessung',
            route: `/${CampusFirstLevelPage.ADMIN}/poll-management`,
            icon: 'poll',
            permission: ChancenPermission.campus.EDIT_POLLS,
          },
        ],
      },
    ],
  },
  ...(A.isDevelopment || baseConfig.get('DEPLOYMENT') === 'staging'
    ? [
        {
          name: 'Tätigkeitserfassung',
          subsections: [
            {
              name: 'Admin',
              links: [
                {
                  label: 'Tätigkeiten',
                  route: CampusRoutes.workTracking,
                },
              ],
            },
          ],
        },
      ]
    : []),
  {
    name: 'CHANCENkurve',
    subsections: [
      {
        name: 'CHANCENkurve',
        links: [
          {
            label: 'Auswertung',
            route: CampusRoutes.diagnostics,
            permission: ChancenPermission.campus.READ_DIAGNOSTIC,
          },
          {
            label: 'Kumulative Auswertung',
            route: CampusRoutes.cumulativeDiagnostics,
            permission: ChancenPermission.campus.READ_DIAGNOSTIC_CUM,
          },
          {
            label: 'Export',
            route: CampusRoutes.export,
            permission: ChancenPermission.campus.EXPORT_LEARNING_DATA,
          },
        ],
      },
    ],
  },
  {
    name: 'Teacher Dashboard',
    subsections: [
      {
        name: '',
        links: [
          {
            label: 'Schülerliste',
            route: CampusRoutes.teacherDashboardStudentList,
            permission: ChancenPermission.campus.VIEW_TEACHER_DASHBOARD,
          },
          {
            label: 'Mitarbeiterliste',
            route: CampusRoutes.teacherDashboardEmployeeList,
            permission: ChancenPermission.campus.VIEW_TEACHER_DASHBOARD_EMPLOYEE,
          },
          {
            label: 'Lernhefte',
            route: CampusRoutes.teacherDashboardHefte,
            permission: ChancenPermission.campus.EDIT_LERNHEFTE,
          },
        ],
      },
    ],
  },
  {
    name: 'Redaktionsansicht',
    subsections: [
      {
        name: 'CHANCENcampus',
        links: [
          {
            label: 'Lerninhalte',
            route: CampusRoutes.lessons,
            permission: ChancenPermission.campus.EDIT_CONTENT,
          },
          {
            label: 'LEVUMI Mathe',
            route: CampusRoutes.levumiMath,
            permission: ChancenPermission.campus.EDIT_CONTENT,
          },
          {
            label: 'LEVUMI Deutsch',
            route: CampusRoutes.levumiGerman,
            permission: ChancenPermission.campus.EDIT_CONTENT,
          },
          {
            label: 'LEVUMI Zahlendiktat',
            route: CampusRoutes.levumiNumberDictation,
            permission: ChancenPermission.campus.EDIT_CONTENT,
          },
        ],
      },
      {
        name: 'CHANCENcorner',
        links: [
          {
            label: 'Inhalte',
            route: CampusRoutes.editorialLounge,
            permission: ChancenPermission.campus.EDIT_CONTENT,
          },
          {
            label: 'Externe Inhalte',
            route: CampusRoutes.editorialLoungeExternal,
            permission: ChancenPermission.campus.EDIT_CONTENT_EXTERNAL,
          },
        ],
      },
    ],
  },
  {
    name: 'Schüler:innenansicht',
    subsections: [
      {
        name: '',
        links: [
          {
            label: 'Mein Bereich',
            route: CampusRoutes.dashboard,
            icon: dashboardLogo,
            permission: ChancenPermission.campus.LOGIN_CAMPUS,
          },
          {
            label: 'Deine Lektionen',
            route: CampusRoutes.yourLessons,
            icon: graduateHatLogo,
            permission: ChancenPermission.campus.READ_CONTENT,
          },
          {
            label: 'Alle Lektionen',
            route: CampusRoutes.allLessons,
            icon: searchLogo,
            permission: ChancenPermission.campus.READ_CONTENT,
          },
          {
            label: 'CHANCENkurve',
            route: CampusRoutes.allLessonsChancenkurve,
            icon: searchLogo,
            permission: ChancenPermission.campus.READ_CONTENT,
          },
          {
            label: 'CHANCENcorner',
            route: CampusRoutes.lounge,
            icon: searchLogo,
            permission: ChancenPermission.campus.READ_LOUNGE,
          },
          {
            label: 'CHANCENcorner (Externe Inhalte)',
            route: CampusRoutes.loungeExternal,
            icon: searchLogo,
            permission: ChancenPermission.campus.READ_LOUNGE_EXTERNAL,
          },
        ],
      },
    ],
  },
];

/**
 * @customElement campus-component-sidebar
 */
@customElement('campus-component-sidebar')
export class CampusComponentSidebar extends ChemistryLitElement {
  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          zIndex: 100,
        },
      },
    ];
  }

  override render() {
    return html`
      <cr-sidebar .logoRouteTo=${CampusRoutes.dashboard} .version=${version}>
        <cr-flex padding="md" slot="logo" w100>
          <cr-image
            disabled
            mode="width"
            ${css({
              width: '230px',
              cursor: 'pointer',
            })}
            .value=${logo}
          ></cr-image>
        </cr-flex>
        <chancen-component-auto-navigation
          .sections=${navigation}
          @onSectionChange=${(e: { detail: Maybe<SidebarSectionDefinition> }) => {
            const link = e.detail?.subsections[0]?.links[0]?.route;
            if (link) {
              goTo(link);
            }
          }}
        ></chancen-component-auto-navigation>
        <cr-flex space="sm" slot="footer" crossaxis-center>
          <cr-flex horizontal>
            <cr-text
              ${css({ cursor: 'pointer', color: 'white', fontSize: '0.8rem' })}
              @click=${() => goTo(CampusRoutes.impress)}
            >
              Impressum
            </cr-text>
            <cr-text ${css({ color: 'white', margin: '0 10px 0 10px' })}>•</cr-text>
            <cr-text
              ${css({ cursor: 'pointer', color: 'white', fontSize: '0.8rem' })}
              @click=${() => goTo(CampusRoutes.privacy)}
            >
              Datenschutz
            </cr-text>
          </cr-flex>
        </cr-flex>
      </cr-sidebar>
    `;
  }
}
